/**
 * CliniciansPage.tsx — the /clinicians surface (CLINICIAN-gated).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY (2026-07-21 CRM redesign): the old Case queue / Callbacks / Form
 * requests tabs are FOLDED INTO the one CRM pipeline (`PipelineTab` — the
 * same board admins work, role-emphasized). The old case queue is the
 * pipeline's "Clinical review" stage; approve/reject/questionnaire happen in
 * the deal drawer. The role gate is applied at the route (RequireRole); the
 * gateway is the real control (R41) — the UI never gates data.
 *
 * NAVIGATION (UX Wave 3a; rebuilt UX Wave 5; polished UX Wave 7B,
 * 2026-07-21 late): sections are chosen from the shared full-height staff
 * RAIL (ui/SideNav — the SAME component the admin console mounts, never a
 * fork): the waffle ClinicSwitcher at the rail top, outline-SVG icon+label
 * items, and the top bar's WHITE masthead block (ui/StaffMasthead) carrying
 * the GPS logo + "Clinician workspace" beside it + the per-section
 * breadcrumb, next to the shared ProfileMenu. No emergency ribbon,
 * centred-logo header or legal footer here (Wave 5 §3/§5 — see
 * ui/chrome.tsx's AppShell); the working pane is FULL-WIDTH fluid so the
 * board gets the whole viewport (§4). The `?tab=` keys are UNCHANGED so
 * existing deep links still land on the right section. Wave 7B §5: picking a
 * NON-LIVE clinic from the waffle swaps the section area for the friendly
 * coming-soon panel with CLINICIAN-appropriate copy (the rail — and the way
 * back — stays), mirroring the admin console.
 *
 * UX WAVE 8 (2026-07-21 night): "Your profile" LEFT the rail — it now lives in
 * the top avatar/account menu only (§2 — ui/chrome.tsx's ProfileMenu links to
 * `?tab=profile`, which this page still renders; the deep link is unchanged).
 * "My activity" became its OWN rail item (§4 — the self-audit panel moved out
 * of My stats, which now holds only the count tiles + daily trend + outcomes).
 * The rebuilt LinkedIn-style profile panel (view↔edit, photo, bio, inline
 * signature image) is PrescriptionProfile.tsx.
 *
 * SAFETY REVIEW (2026-08-20): a "Safety review" rail section sits with the other
 * clinical sections, before the personal "My …" ones. It is where the clinical
 * lead reads the patient-facing risk wording in order to ratify it — the only
 * place in the estate that copy can be read without walking a whole patient
 * journey to a payment screen. It is a SECTION rather than a route of its own
 * because the copy is post-eligibility POM content (R28) and the clinician role
 * gate on /clinicians is what keeps it off the public web. Deep link:
 * `?tab=safety` (SafetyReview.tsx).
 *
 * MONEY-REDACTION: no price/amount/pence anywhere on this surface — asserted in
 * the money-redaction test that renders both the queue and the review.
 */
import { useSearchParams } from 'react-router-dom';
import { ClinicSwitcher } from '../admin/ClinicSwitcher';
import { ComingSoonPanel } from '../admin/ComingSoonPanel';
import { DEFAULT_CLINIC_SLUG, findClinic } from '../admin/clinics';
import { PipelineTab } from '../pipeline/PipelineTab';
import { PatientsTab } from '../pms/PatientsTab';
import { ProfileMenu } from '../ui/chrome';
import { IconActivity, IconPatients, IconPipeline, IconSafety, IconStats } from '../ui/icons';
import { SideNav, SideNavLayout, type SideNavItem } from '../ui/SideNav';
import { StaffMasthead } from '../ui/StaffMasthead';
import { ClinicianAnalytics } from './ClinicianAnalytics';
import { MyActivityLog } from './MyActivityLog';
import { PrescriptionProfile } from './PrescriptionProfile';
import { SafetyReview } from './SafetyReview';

type Tab = 'pipeline' | 'patients' | 'safety' | 'analytics' | 'activity' | 'profile';

/** Icons are decorative (Wave 7B §1 — outline-only inline SVGs, the emoji are
 * gone) — the label carries the accessible name.
 * Wave 6 §5: Patients is the MONEY-FREE PMS variant — the record drawer never
 * offers Subscriptions & payments here and the orders patient-context read is
 * never fired (the money-redaction sweep renders this section too).
 * Wave 8: NO "Your profile" item here — profile lives in the avatar menu (§2);
 * "My activity" (the self-audit view) is its own item (§4). */
const TABS: Array<SideNavItem<Tab>> = [
  { key: 'pipeline', label: 'Pipeline', icon: <IconPipeline /> },
  { key: 'patients', label: 'Patients', icon: <IconPatients /> },
  /* Sits with the CLINICAL sections rather than after the personal "My …" ones:
     it is a shared reference surface about the clinic's treatments, not
     anything about the signed-in clinician. */
  { key: 'safety', label: 'Safety review', icon: <IconSafety /> },
  { key: 'analytics', label: 'My stats', icon: <IconStats /> },
  { key: 'activity', label: 'My activity', icon: <IconActivity /> },
];

function isTab(value: string | null): value is Tab {
  // `profile` stays a VALID deep-link key (the avatar menu's target) even
  // though it no longer appears on the rail.
  return value === 'profile' || TABS.some((t) => t.key === value);
}

export function CliniciansPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // URL-synced (?tab=) so deep links land on the right tab — mirrors AdminPage.
  const tabParam = searchParams.get('tab');
  const tab: Tab = isTab(tabParam) ? tabParam : 'pipeline';
  // The rail waffle drives `?clinic=` here too (Wave 7B §5) — a non-live
  // clinic swaps the section area for the clinician coming-soon panel.
  const clinicSlug = searchParams.get('clinic') ?? DEFAULT_CLINIC_SLUG;
  const clinic = findClinic(clinicSlug);
  const configured = clinic?.configured ?? false;

  function selectTab(key: Tab) {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  }

  const sectionLabel = configured
    ? // Wave 8 §2: profile is off the rail — its breadcrumb label lives here.
      tab === 'profile'
      ? 'Your profile'
      : TABS.find((t) => t.key === tab)?.label ?? 'Pipeline'
    : clinic?.name ?? clinicSlug;

  return (
    <SideNavLayout
      nav={
        <SideNav
          label="Clinician sections"
          // A non-live clinic hides the section items but keeps the rail —
          // the waffle switcher on it is the way back (mirrors AdminPage).
          items={configured ? TABS : []}
          activeKey={tab}
          onSelect={selectTab}
          switcher={<ClinicSwitcher />}
        />
      }
      // Wave 7B: the logo left the green rail for the WHITE masthead block —
      // workspace name beside it, the per-section breadcrumb under it (§6/§8).
      masthead={
        <StaffMasthead
          workspace="Clinician workspace"
          section={sectionLabel}
          homeTo="/clinicians"
          homeLabel="GP Service — clinician home"
        />
      }
      // Sign-out lives in the top bar's profile menu — no in-page duplicate.
      account={<ProfileMenu />}
    >
      {!configured ? (
        <ComingSoonPanel clinicName={clinic?.name ?? clinicSlug} audience="clinician" />
      ) : (
        <>
          {tab === 'pipeline' ? <PipelineTab /> : null}
          {tab === 'patients' ? <PatientsTab variant="clinician" /> : null}
          {/* The clinic follows the rail waffle, so a reviewer reads the wording
              for the clinic they are actually looking at rather than a default. */}
          {tab === 'safety' ? <SafetyReview clinicSlug={clinicSlug} /> : null}
          {tab === 'analytics' ? <ClinicianAnalytics /> : null}
          {/* Wave 8 §4: the self-audit view is its own section. */}
          {tab === 'activity' ? <MyActivityLog /> : null}
          {/* Wave 8 §2: reached from the avatar menu (no rail item). */}
          {tab === 'profile' ? <PrescriptionProfile /> : null}
        </>
      )}
    </SideNavLayout>
  );
}
